import { X, Eye, Building2, Target, Euro, TrendingUp, Users } from 'lucide-react';
import { Agency } from '../../types/agency';
import { formatCurrency } from '../../utils/format';
import { getStatusColor } from '../../utils/colors';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useNegotiatorTransactions } from '../../hooks/useNegotiatorTransactions';
import NegotiatorDetailsModal from './NegotiatorDetailsModal';
import { useState } from 'react';
import { DateRange, getMonthObjectiveLabel } from '../../utils/period';

interface AgencyDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  agency: Agency | null;
  startDate?: string;
  endDate?: string;
  ranges?: DateRange[];
  period?: 'year' | 'quarter' | 'month';
  selectedPeriod?: string;
}

const getObjectiveLabel = (period: 'year' | 'quarter' | 'month' = 'year', selectedPeriod = '1') => {
  if (period === 'year') return 'Objectif Annuel';
  if (period === 'quarter') return 'Objectif Trimestriel';
  return `Objectif ${getMonthObjectiveLabel(selectedPeriod)}`;
};

export default function AgencyDetailsModal({
  isOpen,
  onClose,
  agency,
  startDate,
  endDate,
  ranges,
  period = 'year',
  selectedPeriod = '1'
}: AgencyDetailsModalProps) {
  useEscapeKey(() => { if (isOpen) onClose(); });

  const [selectedNegotiator, setSelectedNegotiator] = useState<{ id: string; name: string } | null>(null);
  const { loadTransactions, loading, error } = useNegotiatorTransactions();
  const [negotiatorTransactions, setNegotiatorTransactions] = useState([]);

  const handleViewSales = async (negotiatorId: string, negotiatorName: string) => {
    setSelectedNegotiator({ id: negotiatorId, name: negotiatorName });
    const transactions = await loadTransactions(negotiatorId, startDate, endDate, agency?.name, ranges);
    setNegotiatorTransactions(transactions);
  };

  if (!isOpen || !agency) return null;

  const remaining = agency.remaining;
  const exceeded = remaining <= 0;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
        <div className="bg-white rounded-2xl w-full max-w-5xl relative max-h-[92vh] flex flex-col shadow-2xl">

          {/* ── Header ── */}
          <div className="relative rounded-t-2xl overflow-hidden bg-gradient-to-r from-slate-800 via-slate-900 to-indigo-950 px-6 py-5 shrink-0">
            <div className="absolute top-0 right-0 w-48 h-48 bg-indigo-500/10 rounded-full -translate-y-1/2 translate-x-1/3 pointer-events-none" />
            <div className="relative flex items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
                  <Building2 size={20} className="text-white/80" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white tracking-tight">{agency.name}</h2>
                  <p className="text-sm text-slate-300 mt-0.5">
                    <span className="text-indigo-300 font-semibold">{agency.negotiator_count}</span>
                    {' '}négociateur{agency.negotiator_count > 1 ? 's' : ''}
                    {' · '}
                    <span className="text-emerald-300 font-semibold">{agency.sales_count}</span>
                    {' '}vente{agency.sales_count > 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors shrink-0"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* ── Scrollable body ── */}
          <div className="overflow-y-auto flex-1 p-6 space-y-6">

            {/* KPIs */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">

              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                    <Target size={15} className="text-indigo-600" />
                  </div>
                </div>
                <p className="text-xs font-medium text-slate-500 mb-1">{getObjectiveLabel(period, selectedPeriod)}</p>
                <p className="text-xl font-bold text-slate-800">{formatCurrency(agency.annual_objective)}</p>
                <div className="mt-2 h-1 bg-indigo-100 rounded-full overflow-hidden">
                  <div className="h-full bg-indigo-400 rounded-full w-full" />
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                    <Euro size={15} className="text-emerald-600" />
                  </div>
                </div>
                <p className="text-xs font-medium text-slate-500 mb-1">CA Total</p>
                <p className="text-xl font-bold text-slate-800">{formatCurrency(agency.total_sales)}</p>
                <div className="mt-2 h-1 bg-emerald-100 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-400 rounded-full w-full" />
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${exceeded ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                    <TrendingUp size={15} className={exceeded ? 'text-emerald-600' : 'text-amber-600'} />
                  </div>
                </div>
                <p className="text-xs font-medium text-slate-500 mb-1">
                  {exceeded ? 'Dépassement' : 'Reste à réaliser'}
                </p>
                <p className={`text-xl font-bold ${exceeded ? 'text-emerald-600' : 'text-amber-700'}`}>
                  {formatCurrency(Math.abs(remaining))}
                </p>
                <div className="mt-2 h-1 rounded-full overflow-hidden bg-slate-100">
                  <div className={`h-full rounded-full ${exceeded ? 'bg-emerald-400' : 'bg-amber-400'} w-full`} />
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center">
                    <Users size={15} className="text-violet-600" />
                  </div>
                </div>
                <p className="text-xs font-medium text-slate-500 mb-1">% Réalisation</p>
                <p className={`text-xl font-bold ${
                  agency.completion_rate >= 100 ? 'text-emerald-600' :
                  agency.completion_rate >= 75  ? 'text-blue-600' :
                  agency.completion_rate >= 50  ? 'text-amber-600' : 'text-red-500'
                }`}>{agency.completion_rate.toFixed(1)}%</p>
                <div className="mt-2 h-1 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${
                      agency.completion_rate >= 100 ? 'bg-emerald-400' :
                      agency.completion_rate >= 75  ? 'bg-blue-400' :
                      agency.completion_rate >= 50  ? 'bg-amber-400' : 'bg-red-400'
                    }`}
                    style={{ width: `${Math.min(agency.completion_rate, 100)}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Table négociateurs */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="flex items-center gap-3 px-5 py-3.5 bg-slate-50 border-b border-slate-100">
                <Users size={15} className="text-slate-500" />
                <span className="text-sm font-semibold text-slate-700">Négociateurs</span>
                <span className="ml-auto text-xs text-slate-400">{agency.negotiators.length} membre{agency.negotiators.length > 1 ? 's' : ''}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      {['Négociateur', 'Statut', getObjectiveLabel(period, selectedPeriod), 'CA Total', '% du CA Agence', 'Nb Ventes', 'Actions'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {agency.negotiators.map((negotiator) => {
                      const pct = agency.total_sales > 0
                        ? (negotiator.total_sales / agency.total_sales) * 100
                        : 0;
                      return (
                        <tr
                          key={negotiator.id}
                          className="border-b border-slate-100 last:border-0 hover:bg-indigo-50/30 transition-colors"
                        >
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-sm font-semibold text-slate-800">
                              {negotiator.firstname} {negotiator.lastname}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full ${getStatusColor(negotiator.status)}`}>
                              {negotiator.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                            {formatCurrency((negotiator as any).annual_objective)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-sm font-bold text-slate-800">
                              {formatCurrency(negotiator.total_sales)}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-2 min-w-[120px]">
                              <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-indigo-500 rounded-full"
                                  style={{ width: `${Math.min(pct, 100)}%` }}
                                />
                              </div>
                              <span className="text-xs font-semibold text-slate-600 w-10 text-right">
                                {pct.toFixed(1)}%
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-50 text-xs font-bold text-emerald-700">
                              {negotiator.sales_count}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <button
                              onClick={() => handleViewSales(negotiator.id, `${negotiator.firstname} ${negotiator.lastname}`)}
                              className="p-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-600 transition-colors"
                              title="Voir les ventes"
                            >
                              <Eye size={14} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        </div>
      </div>

      <NegotiatorDetailsModal
        isOpen={selectedNegotiator !== null}
        onClose={() => setSelectedNegotiator(null)}
        negotiatorName={selectedNegotiator?.name || ''}
        transactions={negotiatorTransactions}
        agencyName={agency.name}
        startDate={startDate || ''}
        endDate={endDate || ''}
        ranges={ranges}
      />
    </>
  );
}
